/**
 * The one `SurfaceServicesProvider` that both host documents install, moved from the shell's
 * `services/create-loader-services.ts` (kernel apps, WS-14 section 3.6; layered shell surfaces,
 * WS-8 section 3.13, R-9, R-22, R-25 e, R-43, R-50).
 *
 * A host builds it once at boot: the shell on `<dp>` (WS-16) and the kernel SPA on `kernel.<dp>`
 * (WS-15). It builds the document-wide controllers once and, per mount, one `MountScope` and one
 * frozen `SurfaceServices` whose every member is scoped to that mount. The runtime calls
 * `servicesFor` after its refusals and before the surface's `mount`, and `releaseServices` after an
 * awaited `unmount()` (R-50), so effect cleanups still see live services. Its only
 * origin-specific input is the {@link OriginFetchPolicy}.
 *
 * | Member | routed root | routed | embedded | system |
 * |---|---|---|---|---|
 * | `polarity.set` | allowed | `root-only` | `root-only` | `root-only` |
 * | `location.navigate` | allowed | allowed | `embedded-navigation` | allowed |
 * | recents | nothing recorded | records `appId` | nothing recorded | nothing recorded |
 * | app-open record | nothing sent | `POST /v1/auth/app-opens` for a valid `appUuid` | nothing sent | nothing sent |
 * | `savedState` | Web Storage | Web Storage | memory | memory |
 * | `portalTarget()` | `element` | `element` | overlay child | `element` (`#root`) |
 * | `settings` | the org's settings | `null` | `null` | `null` |
 * | `slots.fill` | `value-invalid` | the row's `fills` | the row's `fills` | `value-invalid` |
 * | `slots.provide` | the row's `slots`, while the live root | `root-only` | `root-only` | `root-only` |
 *
 * `extensions` and `slots` read the document's one extension point board
 * (`createExtensionPointBoard`, surface extension points, WS-6). The routed root that joined last is
 * the live root (WS-2 W-7).
 *
 * The app-open record (issue #4407, plan 2026-10-22-app-open-hosted-root) goes out per routed
 * non-root mount with a catalog id. So the apps that a root shell mounts get a record, under the
 * hosted `default-shell` too. A root mount itself writes no record. A remount (Retry, a new app
 * version) records again.
 */
import { isNavigablePath } from '@zion/shell-chrome/model';
import type { SurfaceMountRequest, SurfaceServices, SurfaceServicesProvider } from '@zion/sdk-surfaces/v1';
import { createBrandingController, type BrandingController } from './branding.js';
import { createExtensionPointBoard, type ExtensionPointSource } from './extension-points.js';
import { createMountFetch, type OriginFetchPolicy } from './fetch-policy.js';
import {
  SurfaceServiceRefusedError,
  createMountScope,
  createSessionView,
  type MountScopeController,
  type ServiceClock,
  type ServiceSession,
} from './kit.js';
import { createLocationAuthority, type DocumentLocation, type HistorySource } from './navigation.js';
import { createMountPermissions, type MountPermissions } from './permissions.js';
import { createPolarityController, isRoutedRoot, type PolarityController } from './polarity.js';
import { createPortalTargets } from './portal-targets.js';
import { createPreferencesStore } from './preferences.js';
import { recordAppOpen } from './app-open.js';
import { createRecentsController } from './recents.js';
import { bindSavedStateOwner, clearStaleStates, createSavedStateService } from './saved-state.js';
import { createSettingsController, type ShellSettingsSource } from './settings.js';

/** The `appId` of every system mount (D18). */
export const SYSTEM_MOUNT_APP_ID = 'system';

/**
 * What a host hands the provider.
 *
 * - `session`: this origin's session port.
 * - `clock`: `systemClock` in production.
 * - `matchMedia`: the window's `matchMedia`, for the polarity controller.
 * - `history`: the host's one navigation watcher (`createNavigationWatcher(window)`).
 * - `overlay`: the connected `#zion-overlay` element.
 * - `location`: the live `window.location` in production. The location authority reads its three
 *   URL parts, and each mount's `fetch` classifies against its `href` at each call.
 * - `policy`: this origin's fetch rules. Branding, preferences, settings and the app-open record
 *   send through its `platformFetch`. The preferences bearer check reads its `liveIdentity`. Each
 *   mount's app bearer lives through its `retainApp`.
 * - `shellSettings`: the org's settings loader, such as `createRootShellSettingsSource(
 *   policy.platformFetch)`, or `null` for a host with no settings. With `null` the routed root
 *   reads `settings: null` with `resolved: true` (WS-14 section 3.6, change 4).
 * - `extensionPoints`: the registry port of `extensions` and `slots` (surface extension points,
 *   WS-6). The shell passes a source over the registry its loader host resolves from (WS-7). The
 *   kernel SPA passes `null` (D16): `has` answers `false`, `list` answers `[]`, every `fill`
 *   refuses `value-invalid`, and every `provide` refuses `root-only`, or `value-invalid` for the
 *   routed root. An untyped caller that leaves the key out gets the `null` answers, and the
 *   provider still builds (`createExtensionPointBoard`).
 */
export interface SurfaceServicesDeps {
  readonly session: ServiceSession;
  readonly clock: ServiceClock;
  readonly matchMedia: (query: string) => MediaQueryList;
  readonly history: HistorySource;
  readonly overlay: HTMLElement;
  readonly location: DocumentLocation & { readonly href: string };
  readonly policy: OriginFetchPolicy;
  readonly shellSettings: ShellSettingsSource | null;
  readonly extensionPoints: ExtensionPointSource | null;
}

/**
 * The provider plus the host's calls outside the v1 contract.
 *
 * - `servicesFor(request)` throws `TypeError` naming the defect for seven host defects: a
 *   `mountId` already live; a routed `mountPath` `isNavigablePath` refuses; a routed or embedded
 *   `element` whose `data-hosted-app` differs from `appId`; a system request with an `appUuid`,
 *   with a permission key, or with an `appId` other than `system`; a non-system request with
 *   `appId` `system`. After `dispose` it throws `SurfaceServiceRefusedError` `disposed`.
 *   Otherwise it returns one frozen object of the thirteen `SurfaceServices` members in contract
 *   order (`SURFACE_SERVICE_MEMBERS`). Before the return it does these steps:
 *   - It retains the mount's app through `policy.retainApp(request.appUuid)`.
 *   - It records the app in recents for a routed non-root mount.
 *   - It sends the app-open record for that mount through `policy.platformFetch` when its
 *     `appUuid` is not `null`. `recordAppOpen` sends nothing for a value that is not a UUID. It
 *     drops every error of the send, so the record never throws or delays the return.
 *   - It puts the mount on the extension point board, where a routed root takes the live root
 *     claim.
 *   A system mount's `appUuid` is `null`, so it retains nothing.
 * - `releaseServices(mountId)` ends that mount: every member throws or rejects `disposed`, its
 *   subscriptions stop, its written preferences flush, its app retain is released, its overlay
 *   child is removed, its slot fills end, its slot providers stop and it frees the live root claim
 *   when it holds it. An unknown or released id is a no-op.
 * - `updatePermissionKeys(mountId, keys)` replaces a live mount's registry keys after a registry
 *   refetch; its permissions subscribers hear once when the value changed. An unknown or released
 *   id is a no-op.
 * - `rootServices(mountId)` answers the document polarity and branding controllers while
 *   `mountId` is a live routed-root mount, and `null` for any other id. The kernel SPA and the
 *   interim root feed the chrome from it.
 * - `dispose()` releases every mount, then every controller, and unsubscribes from
 *   `extensionPoints`. It never disposes `policy`,
 *   `history` or `session`; their builder does. A second call is a no-op.
 */
export interface SurfaceServicesHost extends SurfaceServicesProvider {
  updatePermissionKeys(mountId: string, keys: readonly string[]): void;
  rootServices(mountId: string): { readonly polarity: PolarityController; readonly branding: BrandingController } | null;
  dispose(): void;
}

interface LiveMount {
  readonly scope: MountScopeController;
  readonly permissions: MountPermissions;
}

/** The first host defect in `request`, or `null` when there is none. */
function hostDefect(request: SurfaceMountRequest, live: ReadonlyMap<string, LiveMount>): string | null {
  if (live.has(request.mountId)) return 'the mount id is already live';
  const { placement } = request;
  if (placement.kind === 'routed' && !isNavigablePath(placement.mountPath)) {
    return 'a routed mountPath must be an absolute same-origin path';
  }
  if (placement.kind === 'system') {
    if (request.appUuid !== null) return 'a system mount carries no appUuid';
    if (request.permissionKeys.length > 0) return 'a system mount carries no permission keys';
    if (request.appId !== SYSTEM_MOUNT_APP_ID) return `a system mount's appId is ${SYSTEM_MOUNT_APP_ID}`;
    return null;
  }
  if (request.appId === SYSTEM_MOUNT_APP_ID) return `only a system mount may use appId ${SYSTEM_MOUNT_APP_ID}`;
  if (request.element.getAttribute('data-hosted-app') !== request.appId) {
    return "the mount element's data-hosted-app must equal appId";
  }
  return null;
}

/**
 * Build the provider.
 *
 * It removes expired saved-state entries (`clearStaleStates`, a refusing `sessionStorage`
 * tolerated), then builds the portal targets, which throw `TypeError` for an overlay other than a
 * connected `#zion-overlay`, then the polarity, branding, preferences, recents, location and
 * settings controllers and the extension point board, which subscribes to `deps.extensionPoints`.
 * It then runs `bindSavedStateOwner` on the current session and on every
 * session change until `dispose`, so a sign-out or an identity change removes the routed saved
 * state of the identity before it. Branding reads `/branding.json` through the global `fetch`
 * with no bearer, and the org branding through `policy.platformFetch`.
 *
 * @throws `TypeError` for an overlay other than a connected `#zion-overlay`.
 */
export function createSurfaceServices(deps: SurfaceServicesDeps): SurfaceServicesHost {
  const { session, clock, policy, location } = deps;
  try {
    clearStaleStates();
  } catch {
    // A refusing sessionStorage holds nothing stale to clear.
  }
  const portals = createPortalTargets(deps.overlay);
  const polarity = createPolarityController(deps.matchMedia);
  const branding = createBrandingController(session, polarity, policy.platformFetch);
  const preferences = createPreferencesStore(session, clock, policy.platformFetch, () => policy.liveIdentity());
  const recents = createRecentsController(preferences, session, clock);
  const locationAuthority = createLocationAuthority(deps.history, location);
  const settings = createSettingsController(session, deps.shellSettings);
  const points = createExtensionPointBoard(deps.extensionPoints);
  // Every identity change removes the previous identity's routed saved state at once, before any
  // reader of the tab's Web Storage sees the new session.
  const stopSavedStateOwner = session.subscribe(() => {
    bindSavedStateOwner(session.current());
  });
  bindSavedStateOwner(session.current());
  const mounts = new Map<string, LiveMount>();
  let disposed = false;

  const release = (mountId: string): void => {
    const mount = mounts.get(mountId);
    if (mount === undefined) return;
    mounts.delete(mountId);
    mount.scope.dispose();
  };

  return {
    servicesFor(request) {
      if (disposed) throw new SurfaceServiceRefusedError('disposed', 'servicesFor after the host ended');
      const defect = hostDefect(request, mounts);
      if (defect !== null) throw new TypeError(`servicesFor: ${defect}`);
      const scope = createMountScope(request);
      const permissions = createMountPermissions(scope, session);
      scope.own(policy.retainApp(request.appUuid));
      const { placement } = request;
      const routedNonRoot = placement.kind === 'routed' && !placement.root;
      if (routedNonRoot) recents.record(request.appId);
      const extensionPoints = points.view(scope);
      const services: SurfaceServices = Object.freeze({
        fetch: createMountFetch(scope, policy, () => location.href),
        permissions: permissions.service,
        polarity: polarity.view(scope),
        branding: branding.view(scope),
        preferences: preferences.view(scope),
        recents: recents.view(scope),
        savedState: createSavedStateService(scope, session),
        location: locationAuthority.view(scope),
        session: createSessionView(scope, session),
        settings: settings.view(scope),
        portalTarget: portals.targetFor(scope),
        extensions: extensionPoints.extensions,
        slots: extensionPoints.slots,
      });
      mounts.set(request.mountId, { scope, permissions });
      // `recordAppOpen` needs a catalog id, so a mount whose `appUuid` is null writes no audit row.
      if (routedNonRoot && request.appUuid !== null) recordAppOpen(policy.platformFetch, request.appUuid);
      return services;
    },
    releaseServices: release,
    updatePermissionKeys(mountId, keys) {
      mounts.get(mountId)?.permissions.updateKeys(keys);
    },
    rootServices(mountId) {
      const mount = mounts.get(mountId);
      if (mount === undefined || !isRoutedRoot(mount.scope)) return null;
      return Object.freeze({ polarity, branding });
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const mountId of Array.from(mounts.keys())) release(mountId);
      stopSavedStateOwner();
      preferences.dispose();
      recents.dispose();
      branding.dispose();
      polarity.dispose();
      locationAuthority.dispose();
      settings.dispose();
      portals.dispose();
      points.dispose();
    },
  };
}
